import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useChatSearch } from '../../../hooks/useChatSearch';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchIcon from '../icons/search.icon';
import '.././../../scss/search-bar.style.scss';

interface DashboardRoute {
  enName: string;
  svName: string;
  path: string;
}

const DASHBOARD_ROUTES: DashboardRoute[] = [
  { enName: 'Dashboard',                    svName: 'Översikt',                       path: '/v2/dashboard' },
  { enName: 'Manage Invoices',              svName: 'Hantera fakturor',               path: '/v2/manage-invoice' },
  { enName: 'Create Invoice',               svName: 'Skapa faktura',                  path: '/v2/create-invoice' },
  { enName: 'Drafted Invoices',             svName: 'Fakturautkast',                  path: '/v2/draft-invoice' },
  { enName: 'My Clients',                   svName: 'Mina kunder',                    path: '/v2/my-clients' },
  { enName: 'My Freelancers',               svName: 'Mina frilansare',                path: '/v2/my-freelancers' },
  { enName: 'Contracts and Agreements',     svName: 'Avtal och överenskommelser',     path: '/v2/my-contracts' },
  { enName: 'Employer Certificate',         svName: 'Arbetsgivarintyg',              path: '/v2/employer-certificate' },
  { enName: 'Timesheets',                   svName: 'Tidrapporter',                   path: '/v2/timesheets-invoice' },
  { enName: 'Deductions',                   svName: 'Avdrag',                         path: '/v2/deduction-invoice' },
  { enName: 'Annual Income',                svName: 'Årsinkomst',                     path: '/v2/statements-invoice' },
  { enName: 'Salary Notices',               svName: 'Lönebesked',                     path: '/v2/salary-notice' },
  { enName: 'Salary Calculator',            svName: 'Räkna ut din lön',              path: '/v2/salary-calculator' },
  { enName: 'Health & Private Insurance',   svName: 'Hälsa & Privatförsäkring',       path: '/v2/health-insurance' },
  { enName: 'Assignment Registration',      svName: 'Uppdragsanmälan',               path: '/v2/pre-insurance' },
  { enName: 'FAQ',                          svName: 'FAQ',                            path: '/v2/faq' },
  { enName: 'Employment Contract',          svName: 'Anställningsavtal',             path: '/v2/employment-contract' },
  { enName: 'Profile Settings',             svName: 'Profilinställningar',            path: '/profile-settings' },
  { enName: 'Loyalty Program',              svName: 'Lojalitetsprogram',              path: '/loyalty-program' },
];


interface ChatSearchBarProps {
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const ChatSearchBar: React.FC<ChatSearchBarProps> = ({
  placeholder = 'Search',
  className = '',
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const language = new URLSearchParams(window.location.search).get('lang') || 'en-us';
  const navigate = useNavigate();
    const { t } = useTranslation();
  const {
    chatData,
    loading,
    fetchChatSearch,
    showDropdown,
    setDropdown
  } = useChatSearch();

  const isSwedish = language === 'sv-se';

  const filteredRoutes = useMemo<DashboardRoute[]>(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return [];
    return DASHBOARD_ROUTES.filter((r) =>
      (isSwedish ? r.svName : r.enName).toLowerCase().includes(query)
    );
  }, [searchValue, isSwedish]);

  useEffect(() => {
    if (searchValue.trim().length === 0) {
      setDropdown(false);
    }
  }, [searchValue, setDropdown]);


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setDropdown]);

  // Focus the search input from anywhere with Ctrl+K (Windows/Linux) or ⌘K (Mac)
  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    document.addEventListener('keydown', handleShortcut);
    return () => {
      document.removeEventListener('keydown', handleShortcut);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (e.target.value.trim().length > 0) {
      setDropdown(true);
      fetchChatSearch({ query: e.target.value });
    } else {
      setDropdown(false);
    }
  };

  const handleSelect = (isCustomer: boolean) => {
    if (isCustomer) {
    navigate(`/v2/my-clients?lang=${language}&customerSearch=` + encodeURIComponent(searchValue));
    }
    else {
      navigate(`/v2/manage-invoice?lang=${language}&invoiceSearch=` + decodeURIComponent(searchValue.trim()));
    }
    setDropdown(false);
    setSearchValue('');
  };

  const handleRouteSelect = (path: string) => {
    navigate(`${path}?lang=${language}`);
    setDropdown(false);
    setSearchValue('');
  };

  return (
    <div className={`search-bar ${className}`} ref={dropdownRef}>
      <div className="search-bar__input-container">
        <SearchIcon className="search-bar__icon" />
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={(e) => handleInputChange(e)}
          placeholder={placeholder}
          disabled={disabled}
          className="search-bar__input"
          aria-label="Search input"
        />
        <kbd className="search-bar__shortcut" aria-hidden="true">⌘K</kbd>
      </div>
      {showDropdown && (
        <div className="search-bar__dropdown">
          {filteredRoutes.length > 0 && filteredRoutes.map((route) => (
            <div
              key={route.path}
              className="search-bar__dropdown-item"
              onClick={() => handleRouteSelect(route.path)}
            >
              <span className="search-bar__dropdown-item-name">{isSwedish ? route.svName : route.enName}</span>
              <span className="search-bar__dropdown-item-desc">{t('PAGE')}</span>
            </div>
          ))}
          {loading ? (
            <div className="search-bar__dropdown-item search-bar__dropdown-item--muted">{t('Loading...')}</div>
          ) : chatData && Array.isArray(chatData.response) ? (
            chatData.response.length === 0 && filteredRoutes.length === 0 ? (
              <div className="search-bar__dropdown-item search-bar__dropdown-item--muted">{t('No results found')}</div>
            ) : (
              chatData.response.map((item: any, idx: number) => (
                <div
                  key={item.name + idx}
                  className="search-bar__dropdown-item"
                  onClick={() => handleSelect(item.isCustomer)}
                >
                  <span className="search-bar__dropdown-item-name">{item.name}</span>
                  <span className="search-bar__dropdown-item-desc">{item.isCustomer ? 'Customer' : 'Invoice'}</span>
                </div>
              ))
            )
          ) : filteredRoutes.length === 0 ? (
            <div className="search-bar__dropdown-item search-bar__dropdown-item--muted">{t('No results found')}</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ChatSearchBar;
